"use client"
import { useEffect, useState } from "react";
import { Breadcrumb } from "../../components/ui/breadcrumb";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { useNuisibles } from "../../hooks/api/nuisibles/index";
import { NuisibleDetail } from "./nuisible-detail";
import { NuisibleForm } from "./nuisible-form";
import { NuisibleList } from "./nuisible-list";

enum View {
  LIST = "list",
  DETAIL = "detail",
  CREATE = "create",
  EDIT = "edit",
}

export function NuisibleManagement() {
  const [currentView, setCurrentView] = useState<View>(View.LIST);
  const [selectedNuisibleId, setSelectedNuisibleId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);

  // Debounce search term to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300); // 300ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, showDeleted]);

  // Fetch nuisibles with search term
  const { data: nuisiblesData, isLoading, error, refetch } = useNuisibles({
    page,
    limit,
    search: debouncedSearch || undefined,
    includeDeleted: showDeleted,
    sortBy: sortBy as "recent" | "oldest" | "alphanum" | undefined
  });

  const handleCreateClick = () => {
    setCurrentView(View.CREATE);
    setSelectedNuisibleId(null);
  };

  const handleEditClick = (id: number) => {
    setSelectedNuisibleId(id);
    setCurrentView(View.EDIT);
  };

  const handleViewClick = (id: number) => {
    setSelectedNuisibleId(id);
    setCurrentView(View.DETAIL);
  };

  const handleBackToList = () => {
    setCurrentView(View.LIST);
    setSelectedNuisibleId(null);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const handleFormSuccess = () => {
    setCurrentView(View.LIST);
    setSelectedNuisibleId(null);
    refetch();
  };

  const handleSortChange = (value: string) => {
    setSortBy(value);
  };

  const getViewTitle = () => {
    switch (currentView) {
      case View.CREATE:
        return "Créer un nuisible";
      case View.EDIT:
        return "Modifier le nuisible";
      case View.DETAIL:
        return "Détails du nuisible";
      default:
        return "Gestion des nuisibles";
    }
  };

  const getBreadcrumbItems = () => {
    const items = [
      { label: "Accueil", href: "/" },
      { label: "Gestion des nuisibles", href: "/nuisibles" }
    ];

    if (currentView === View.CREATE) {
      items.push({ label: "Créer", href: "/nuisibles/create" });
    } else if (currentView === View.EDIT) {
      items.push({ label: "Modifier", href: `/nuisibles/${selectedNuisibleId}/edit` });
    } else if (currentView === View.DETAIL) {
      items.push({ label: "Détails", href: `/nuisibles/${selectedNuisibleId}` });
    }

    return items;
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb items={getBreadcrumbItems()} />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{getViewTitle()}</h1>
          <p className="text-muted-foreground">
            {currentView === View.LIST
              ? "Gérez les nuisibles et leurs propriétés."
              : currentView === View.CREATE
                ? "Ajoutez un nouveau nuisible à la base de données."
                : currentView === View.EDIT
                  ? "Modifiez les informations du nuisible sélectionné."
                  : "Consultez les détails du nuisible sélectionné."}
          </p>
        </div>
        {currentView === View.LIST && (
          <Button onClick={handleCreateClick}>
            Créer un nuisible
          </Button>
        )}
      </div>

      {/* Content */}
      <Card>
        {currentView === View.LIST && (
          <NuisibleList
            nuisibles={nuisiblesData?.data || []}
            isLoading={isLoading}
            error={error}
            onView={handleViewClick}
            onEdit={handleEditClick}
            page={page}
            limit={limit}
            total={nuisiblesData?.meta.total || 0}
            sortBy={sortBy}
            onLimitChange={setLimit}
            onSearchChange={handleSearch}
            searchTerm={searchTerm}
            onPageChange={setPage}
            handleSortChange={handleSortChange}
            showDeleted={showDeleted}
            pageCount={nuisiblesData?.meta.totalPages || 0}
            onShowDeletedChange={setShowDeleted}
          />
        )}

        {currentView === View.CREATE && (
          <NuisibleForm
            onCancel={handleBackToList}
            onSuccess={handleFormSuccess}
          />
        )}

        {currentView === View.EDIT && selectedNuisibleId && (
          <NuisibleForm
            nuisibleId={selectedNuisibleId}
            onCancel={handleBackToList}
            onSuccess={handleFormSuccess}
          />
        )}

        {currentView === View.DETAIL && selectedNuisibleId && (
          <NuisibleDetail
            nuisibleId={selectedNuisibleId}
            onBack={handleBackToList}
            onEdit={handleEditClick}
          />
        )}
      </Card>
    </div>
  );
}
